/**
 * Shared dashed migration-trail reveal — opacity appear L→R.
 * Used by Intro1 / Population Trends / Red List Index / Conclusion / Swirl.
 *
 * Pace is duration-based (not fixed per-dash stagger) so every trail completes
 * in the same wall-clock time regardless of dash count. Timing lives in
 * `src/motion/tokens.ts` (`dashedTrail`).
 */

import { dashedTrail } from '@/motion/tokens';

/** Matches Figma dashed trails (`strokeDasharray="8 8"`). */
export const DASHED_TRAIL_DASH = 8;
export const DASHED_TRAIL_GAP = 8;

export const DASHED_TRAIL_STROKE_WIDTH = 16;
export const DASHED_TRAIL_OPACITY = 0.7;

/**
 * Extra user-unit padding when culling dashes to the viewBox so edge dashes
 * that clip the frame still appear.
 */
export const DASHED_TRAIL_CULL_PADDING = 50;

/** Wall-clock span from first dash start → last dash start (seconds). */
export const DASHED_TRAIL_REVEAL_DURATION_S = dashedTrail.revealDurationS;

/** Per-dash appear duration (seconds) — opacity only, no transform. */
export const DASHED_TRAIL_POP_DURATION_S = dashedTrail.popDurationS;

/** Opacity appear ease (Motion cubic-bezier). */
export const DASHED_TRAIL_POP_EASE = dashedTrail.popEase;

/**
 * Fraction of the SVG that must intersect the viewport to start the reveal.
 * Low so the wave begins as soon as the trail enters (come in earlier).
 */
export const DASHED_TRAIL_IN_VIEW_AMOUNT = dashedTrail.inViewAmount;

/**
 * Fraction for `readySelector` gates — earlier than the default used by other
 * section reveals.
 */
export const DASHED_TRAIL_READY_AMOUNT = dashedTrail.readyAmount;

/** Delay for dash `index` in a trail of `count` dashes (L→R). */
export function dashedTrailDelayAtIndex(index: number, count: number): number {
  if (count <= 1) return 0;
  return (index / (count - 1)) * DASHED_TRAIL_REVEAL_DURATION_S;
}
